import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter as Router } from 'react-router-dom';
import { AuthenticationProvider, HttpErrorPage, NavigationBlocker, SessionTimeout, XHRInterceptor } from '@paycor/pacl';
import axios from 'axios';
import App from './App';
import { HcmConfiguration, PathsConfiguration } from './common/interfaces/PathsConfiguration';
import './index.css';
import { RuntimeContextProvider } from './common/RuntimeContext';
import { UserContextProvider } from './common/UserContext';
import { AuthenticatedUserContextProvider } from './common/AuthenticatedUser';
import { PendoService } from './utilities/pendo.service';

// Typings for inline javascript in index.html (thanks NewRelic...)
declare function initializeLogging(configKey: string, licenseKey: string): void;

function isLocalRuntime() {
    const localHosts = new Set(['localhost', '127.0.0.1', '::1', 'local.paycor.com']);
    return localHosts.has(window.location.hostname);
}

// We need to load our configuration as the first thing we do.
// This will allow us to dynamically configure our front end based on per-environment changes.
axios
    .get(`/paths/paths-sls-api-v2/config?t=${Date.now()}`)
    .then((response) => {
        const config = response.data.config as PathsConfiguration;
        initializeLogging(config.newRelic.applicationId, config.newRelic.licenseKey);

        // For local development only
        if (isLocalRuntime()) {
            config.hcm = {
                ui: {
                    urlBase: 'https://local.paycor.com:3000',
                    prod: false
                }
            };
            config.paycorApi.urlBase = 'https://local.paycor.com:3000';
            config.app.ui.urlBase = 'https://local.paycor.com:3000/paths/';
            config.pathsWeb.serverUrl = 'https://local.paycor.com:3000/paths/paths-sls-api-v2';
            config.pathsWeb.adminServerUrl = 'https://local.paycor.com:3000/paths/paths-api';
            config.pathsWeb.algoliaAppID = 'TXS3TRP0IQ';
            config.pathsWeb.mediaLibraryUrl = 'https://local.paycor.com:3000/paths/paths-sls-api-v2/media';
        }

        // Pendo Initialization
        const pendoService = new PendoService({
            pendoApiKey: config.pendoApiKey
        });
        pendoService
            .initializePendoForAuthenticatedUser({
                urlBase: config.paycorApi.urlBase,
                apimKey: config.authentication.apimSubscriptionKey
            })
            .then(() => {
                // This will intercept any 401 responses from HTTP/XHR calls and redirect the user to the login screen. Additional
                // response handlers can be defined, or the default 401 overwritten
                XHRInterceptor
                    .createInterceptor
                    // {
                    //   403: () => {
                    //     your logic here;
                    //   }
                    // }
                    ();

                // save the apimSubscriptionKey for silent_renew.html
                sessionStorage.setItem('apimSubscriptionKey', config.authentication.apimSubscriptionKey);

                const authConfig = {
                    authority_url: `${config.paycorApi.urlBase}/sts/v1/common`,
                    callback_url: `${config.app.ui.urlBase}oauth/silent_renew.html`,
                    client_id: config.authentication.clientId,
                    scope: config.authentication.scopes.join(' '),
                    APIm: config.authentication.apimSubscriptionKey
                };

                injectGlobalHeader(document, config.hcm);

                ReactDOM.render(
                    <React.StrictMode>
                        <RuntimeContextProvider pathsConfiguration={config}>
                            <AuthenticationProvider settings={authConfig}>
                                <HttpErrorPage>
                                    <SessionTimeout
                                        applicationUrl={config.hcm.ui.urlBase}
                                        refreshUrl={`${config.hcm.ui.urlBase}/accounts/`}
                                        APIm={authConfig.APIm}
                                    >
                                        <UserContextProvider>
                                            <AuthenticatedUserContextProvider>
                                                <Router basename={'/paths'}>
                                                    <NavigationBlocker>
                                                        <App />
                                                    </NavigationBlocker>
                                                </Router>
                                            </AuthenticatedUserContextProvider>
                                        </UserContextProvider>
                                    </SessionTimeout>
                                </HttpErrorPage>
                            </AuthenticationProvider>
                        </RuntimeContextProvider>
                    </React.StrictMode>,
                    document.querySelector('#root')
                );
            })
            .catch(console.log);
    })
    .catch(console.log);

/*
Due to CORS impacts, the global navigation header will only display on QSB and above.
If environment is under QSB, a mock header will appear
*/
function injectGlobalHeader(document: Document, hcm: HcmConfiguration) {
    const headerScript = document.createElement('script');
    headerScript.src = `${hcm.ui.urlBase}/navigation/globalheader${hcm.ui.prod ? '' : '-mock'}.bundle.js`;
    headerScript.id = 'global-header';
    document.querySelector('#perform-global-header')?.append(headerScript);
}
